import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  List, 
  Tag, 
  Button, 
  Input, 
  Select, 
  Row, 
  Col, 
  Spin, 
  Alert,
  Empty,
  Space,
  Badge
} from 'antd'
import { 
  SearchOutlined, 
  CalendarOutlined, 
  DollarOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined 
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import dayjs from 'dayjs'

const { Title } = Typography
const { Option } = Select

interface Estimate {
  id: string
  estimateNumber: string
  title: string
  description: string
  status: string
  hasMultipleOptions: boolean
  option1Title?: string
  option1Total?: number
  option2Title?: string
  option2Total?: number
  option3Title?: string
  option3Total?: number
  selectedOption?: number
  validUntil?: string
  createdAt: string
  approvedAt?: string
  business: {
    id: string
    name: string
    email: string
    phone: string
  }
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    'draft': 'default',
    'pending': 'blue',
    'approved': 'green',
    'rejected': 'red',
    'expired': 'orange'
  }
  return colors[status] || 'default'
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'approved':
      return <CheckCircleOutlined />
    case 'pending':
      return <ClockCircleOutlined />
    default:
      return null
  }
}

export const EstimateList: React.FC = () => {
  const navigate = useNavigate()
  const [estimates, setEstimates] = useState<Estimate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [filteredEstimates, setFilteredEstimates] = useState<Estimate[]>([])

  useEffect(() => {
    loadEstimates()
  }, [])

  useEffect(() => {
    filterEstimates()
  }, [estimates, searchTerm, statusFilter])

  const loadEstimates = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await customerApi.getEstimates()
      setEstimates(response.estimates || [])
    } catch (err: any) {
      console.error('Failed to load estimates:', err)
      setError(err.response?.data?.message || 'Failed to load estimates')
    } finally {
      setLoading(false)
    }
  }

  const filterEstimates = () => {
    let filtered = estimates

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(estimate => estimate.status === statusFilter)
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(estimate => 
        estimate.title.toLowerCase().includes(term) ||
        estimate.estimateNumber.toLowerCase().includes(term) ||
        estimate.description?.toLowerCase().includes(term)
      )
    }

    setFilteredEstimates(filtered)
  }

  const handleViewEstimate = (estimateId: string) => {
    navigate(`/estimates/${estimateId}`)
  }

  const getEstimateTotal = (estimate: Estimate) => {
    if (estimate.selectedOption === 1 && estimate.option1Total) {
      return estimate.option1Total
    } else if (estimate.selectedOption === 2 && estimate.option2Total) {
      return estimate.option2Total
    } else if (estimate.selectedOption === 3 && estimate.option3Total) {
      return estimate.option3Total
    } else if (estimate.option1Total) {
      return estimate.option1Total
    }
    return 0
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Loading your estimates...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Estimates"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={loadEstimates}>
            Retry
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>My Estimates</Title>
        </Col>
        <Col>
          <Badge count={estimates.length} showZero>
            <Button onClick={loadEstimates}>Refresh</Button>
          </Badge>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search estimates..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="all">All Statuses</Option>
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
              <Option value="expired">Expired</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Estimate List */}
      {filteredEstimates.length === 0 ? (
        <Card>
          <Empty
            description={
              estimates.length === 0 
                ? "No estimates found. Your estimates will appear here."
                : "No estimates match your search criteria."
            }
          />
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
          dataSource={filteredEstimates}
          renderItem={(estimate) => (
            <List.Item>
              <Card
                hoverable
                actions={[
                  <Button 
                    type={estimate.status === 'pending' ? 'primary' : 'default'}
                    icon={<EyeOutlined />}
                    onClick={() => handleViewEstimate(estimate.id)}
                  >
                    {estimate.status === 'pending' ? 'Review & Approve' : 'View Details'}
                  </Button>
                ]}
              >
                <Card.Meta
                  title={
                    <Space>
                      <span>{estimate.title}</span>
                      <Tag color={getStatusColor(estimate.status)} icon={getStatusIcon(estimate.status)}>
                        {estimate.status.toUpperCase()}
                      </Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <p><strong>Estimate #{estimate.estimateNumber}</strong></p>
                      {estimate.description && <p>{estimate.description}</p>}
                      
                      <Space direction="vertical" size="small" style={{ width: '100%', marginTop: '12px' }}>
                        <div>
                          <DollarOutlined /> Total: ${Number(getEstimateTotal(estimate)).toFixed(2)}
                        </div>

                        {estimate.hasMultipleOptions && (
                          <div>
                            <Tag color="blue">{estimate.option1Title || 'Option 1'}: ${Number(estimate.option1Total || 0).toFixed(2)}</Tag>
                            {estimate.option2Total && (
                              <Tag color="blue">{estimate.option2Title || 'Option 2'}: ${Number(estimate.option2Total).toFixed(2)}</Tag>
                            )}
                            {estimate.option3Total && (
                              <Tag color="blue">{estimate.option3Title || 'Option 3'}: ${Number(estimate.option3Total).toFixed(2)}</Tag>
                            )}
                          </div>
                        )}

                        {estimate.validUntil && (
                          <div>
                            <CalendarOutlined /> Valid Until: {dayjs(estimate.validUntil).format('MMM D, YYYY')}
                          </div>
                        )}

                        {estimate.approvedAt && (
                          <div>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} /> Approved: {dayjs(estimate.approvedAt).format('MMM D, YYYY')}
                          </div>
                        )}

                        <div style={{ marginTop: '8px' }}>
                          <small style={{ color: '#8c8c8c' }}>
                            Created: {dayjs(estimate.createdAt).format('MMM D, YYYY')}
                          </small>
                        </div>
                      </Space>
                    </div>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
      )}
    </div>
  )
}

